<template>
  <div id="CampaignEditOffersEdit">
    <ValidationObserver v-slot="{ invalid }" slim>

      <Modal
        :title="$t('campaignEdit.offer.Upload Single Use Codes')"
        :visible="codeUploadModalVisible"
        :loading="codeState === 'uploading'"
        @close="codeUploadModalVisible = false"
      >
        <form v-if="!codeState" enctype="multipart/form-data">
          <label>{{$t('campaignEdit.offer.Choose a file of codes to upload')}}:</label><br/>
          <div class="file-selector">
            <input type="file" ref="file" @change="onSelectFile">
          </div>
        </form>
        <div v-else-if="codeState === 'uploaded'">
          <label>{{$t('campaignEdit.offer.Preview of codes')}}:</label>
          <List class="preview-list" tight>
            <ListItem v-for="(code, index) in codePreview" :key="index">{{code}}</ListItem>
          </List> 
          <p class="modal-add-message">{{$t('campaignEdit.offer.addCodesMessage', { count: `${codeCount}`, fileName: `${file.name}` })}}</p>
          <Button class="modal-button" type="primary" @click.prevent="insertCodes">{{$t('Add')}}</Button>
        </div>
      </Modal>

      <Page
        :title="$t('Editing Offer')"
        :primaryAction="{
          label: $t('Save'),
          onAction: () => save(),
          disabled: invalid || !!variantError || saving
        }"
        :breadcrumbs="hasChanged ? [{
          label: $t('Cancel'),
          onAction: () => backToList()
        }] : [{
          label: $t('Back to list'),
          onAction: () => backToList()
        }]"
      > 
        <campaign-edit-header :campaign="campaign"></campaign-edit-header>
        <LoadingLayout v-if="loading" />
        <Layout v-if="!loading">
          <LayoutSection size="primary">
            <Card :title="$t('Value')">
              <CardSection>
                <FormLayout>
                  <ChoiceList
                    :label="$t('Type')"
                    :options="[
                      {
                        label: $t('Percentage'),
                        helpText: $t('campaignEdit.offer.typePercentageHelp'),
                        value: '%'
                      },
                      {
                        label: $t('Fixed'),
                        helpText: $t('campaignEdit.offer.typeFixedHelp'),
                        value: '$'
                      },
                      {
                        label: $t('Free Shipping'),
                        helpText: $t('campaignEdit.offer.typeFreeShippingHelp'),
                        value: 's'
                      },
                      {
                        label: $t('Custom'),
                        helpText: $t('campaignEdit.offer.typeCustomHelp'),
                        value: 't'
                      }
                    ]"
                    v-model="currentIncentive.valueType"
                  />
                </FormLayout>
              </CardSection>
              <CardSection
                v-if="currentIncentive.valueType === '%'"
              >
                <validation-provider :name="$t('Discount Value')" immediate rules="required" v-slot="{ errors }">
                  <TextField
                    :label="$t('Discount Value')"
                    type="number"
                    placeholder="0.00"
                    suffix="%"
                    v-model="currentIncentive.value"
                    :error="errors[0]"   
                  />
                </validation-provider>
              </CardSection>
              <CardSection
                v-if="currentIncentive.valueType === '$'"
              >
                <validation-provider :name="$t('Discount Value')" immediate rules="required" v-slot="{ errors }">
                  <TextField
                    :label="$t('Discount Value')"
                    type="number"
                    placeholder="0.00"
                    :prefix="currentIncentive.valueSymbol || '$'"
                    v-model="currentIncentive.value"
                    :error="errors[0]"
                  />
                </validation-provider>
              </CardSection>
              <CardSection
                v-if="currentIncentive.valueType === 't'"
              >
                <validation-provider :name="$t('Discount Value')" immediate rules="required" v-slot="{ errors }">
                  <TextField
                    :label="$t('Discount Value')"
                    :placeholder="$t('Text Offer')"
                    v-model="currentIncentive.value"
                    :error="errors[0]"
                  />
                </validation-provider>
              </CardSection>
              <CardSection>
                <validation-provider :name="$t('campaignEdit.offer.anticipatedRetailValue')" immediate rules="required" v-slot="{ errors }">
                  <TextField
                    :label="$t('campaignEdit.offer.anticipatedRetailValue')"
                    :help-text="$t('campaignEdit.offer.anticipatedValueHelp')"
                    type="number"
                    placeholder="0.00"
                    :prefix="currentIncentive.valueSymbol || '$'"
                    v-model="currentIncentive.notionalValue"
                    :error="errors[0]"
                    :disabled="currentIncentive.valueType === '$'"
                  />
                </validation-provider>
              </CardSection>
            </Card>
            <Card :title="$t('Offer Text')">
              <CardSection>
                <FormLayout>
                  <ChoiceList
                    :options="[
                      {
                        label: $t('Use Default'),
                        value: false
                      },
                      {
                        label: $t('Custom text'),
                        value: true
                      }
                    ]"
                    v-model="useCustomDisplayText"
                  />
                  <TextField
                    :help-text="$t('campaignEdit.offer.textHelp')"
                    v-model="currentIncentive.displayText"
                    :disabled="!useCustomDisplayText"
                  />
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('Discount Code')">
              <CardSection v-if="singleUseCouponsEnabled">
                <FormLayout>
                  <ChoiceList
                    :options="[
                      {
                        label: $t('Fixed code'),
                        helpText: $t('campaignEdit.offer.fixedCode'),
                        value: 'code'
                      },
                      {
                        label: $t('Single-use'),
                        helpText: $t('campaignEdit.offer.singleUseCode'),
                        value: 'file'
                      }
                    ]"
                    v-model="currentIncentive.promoType"
                  />
                </FormLayout>
              </CardSection>
              <CardSection
                v-if="currentIncentive.promoType === 'code'"
                :title="$t('Reusable code')"
              >
                <validation-provider :name="$t('Discount Code')" immediate rules="required" v-slot="{ errors }">
                  <TextField
                    :label="$t('Discount Code')"
                    v-model="currentIncentive.promoCode"
                    :help-text="$t('campaignEdit.offer.codeHelp')"
                    :error="errors[0]"
                  />
                </validation-provider>
              </CardSection>
              <CardSection
                v-if="currentIncentive.promoType === 'file'"
                :title="$t('Single-use codes')"
              >
                <TextContainer>
                  <Formatter type="integer" :value="currentIncentive.maxRewards === -1 ? 0 : currentIncentive.maxRewards" /> {{ $t('codes defined') }}.
                </TextContainer>
                <br>
                <Button @click.prevent="showCodeModal" :disabled="isCreating" fullWidth>{{ $t('Upload codes') }}</Button>
                <InlineError v-if="isCreating">{{$t('campaignEdit.offer.mustSaveOffer')}}</InlineError>
              </CardSection>
            </Card>
            <Card :title="$t('Minimum requirements')">
              <CardSection>
                <FormLayout>
                  <ChoiceList
                    :options="[
                      {
                        label: $t('None'),
                        value: 'none'
                      },
                      {
                        label: $t('Minimum purchase amount') + ` (${currentIncentive.valueSymbol})`,
                        value: 'amount',
                        content: $createElement('TextField', {
                          props: {
                            prefix: currentIncentive.valueSymbol,
                            placeholder: '0.00',
                            type: 'number',
                            helpText: $t('campaignEdit.offer.minimumHelp'),
                            value: currentIncentive.minPurchase
                          },
                          on: {
                            input: (value) => { currentIncentive.minPurchase = value}
                          }
                        })
                      }
                    ]"
                    v-model="minimumValue"
                  />
                </FormLayout>
              </CardSection>
            </Card>
            <Card v-if="currentIncentive.promoType === 'code'" :title="$t('campaignEdit.offer.usageLimit')">
              <CardSection>
                <FormLayout>
                  <ChoiceList
                    :options="[
                      {
                        label: $t('None'),
                        value: 'none'
                      },
                      {
                        label: $t('Fixed limit'),
                        value: 'fixed',
                        content: $createElement('TextField', {
                          props: {
                            placeholder: '0',
                            type: 'number',
                            helpText: $t('campaignEdit.offer.limitHelp'),
                            value: currentIncentive.maxRewards
                          },
                          on: {
                            input: (value) => { currentIncentive.maxRewards = value }
                          }
                        })
                      }
                    ]"
                    v-model="limitType"
                  />
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('Active dates')">
              <CardSection>
                <FormLayout>
                  <FormLayoutGroup>
                    <DatePicker
                      :label="$t('Validity Period Start')"
                      :placeholder="$t('Valid Immediately')"
                      with-time
                      clearable
                      v-model="currentIncentive.validFromDate"
                    />
                    <DatePicker
                      :label="$t('Validity Period End')"
                      :placeholder="$t('Never Expires')"
                      with-time
                      clearable
                      v-model="currentIncentive.expiryDate"
                    />
                  </FormLayoutGroup>
                  <TimezonePicker
                    :label="$t('Timezone')"
                    v-model="currentIncentive.timezone"
                  />
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('Other')">
              <CardSection>
                <FormLayout>
                  <FormLayoutGroup>
                    <Select
                      :label="$t('Language')"
                      v-model="currentIncentive.language"
                      :options="languages"
                    />
                    <Select v-if="currentIncentive.settings"
                      :label="$t('Variant')"
                      :placeholder="$t('Select Variant')"
                      :options="formattedVariants"
                      v-model="currentIncentive.settings.classes"
                      :error="variantError"
                    />
                  </FormLayoutGroup>
                </FormLayout>
                <FormLayout>
                  <FormLayoutGroup>
                    <div>
                      <TextField
                        :label="$t('Additional Conditions')"
                        v-model="currentIncentive.condition"
                        multiline
                        :error="conditionLengthError"
                      />
                      <div>
                        {{ currentConditionLength }} / {{ maxConditionLength }}
                      </div>
                    </div>
                  </FormLayoutGroup>
                </FormLayout>
                <FormLayout>
                  <ImageInput
                    :label="$t('Image')"
                    v-model="currentIncentive.imageFile"
                  />
                </FormLayout>
              </CardSection>
            </Card>
          </LayoutSection>
          <LayoutSection size="secondary">
            <Card
              :title="$t('Summary')"
              subdued
            >
              <CardSection>
                <List>
                  <ListItem v-if="currentIncentive.maxRewards !== -1">{{$t('campaignEdit.offer.presentedCodesOfTotal', { count: `${currentIncentive.numRewardsUsed ? currentIncentive.numRewardsUsed : '0'}`, max: `${currentIncentive.maxRewards}` })}}</ListItem>
                  <ListItem v-else>{{$t('campaignEdit.offer.presentedCodes', { count: `${currentIncentive.numRewardsUsed ? currentIncentive.numRewardsUsed : '0'}` })}}</ListItem>
                </List>
              </CardSection>
              <CardSection :title="$t('References')">
                <List :emptyText="$t('No Segments Referenced')">
                  <ListItem v-for="seg in referencedSegments" :key="seg.id">
                    <router-link :to='`/campaigns/${campaign.id}/segmentation/segment/${seg.id}`' target="_blank">{{seg.name}}</router-link>
                  </ListItem>
                </List>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>
        <PageActions
          :primaryAction="{
            label: $t('Save'),
            onAction: () => save(),
            disabled: invalid || !!variantError || saving
          }"
          :secondaryActions="hasChanged ? [{
            label: $t('Cancel'),
            type: 'destructive',
            onAction: () => backToList()
          }] : [{
            label: $t('Back to list'),
            onAction: () => backToList()
          }]"
        />
      </Page>
    </ValidationObserver>
    <!-- <pre>{{currentIncentive}}</pre> -->
  </div>
</template>

<script>

import ImageInput from '../components/ui/ImageInput'
import _ from 'lodash'
import CampaignEditHeader from '@/components/CampaignEditHeader'

const MAX_FILE_SIZE = 1024 * 100000;

export default {
  name: 'CampaignEditOffersEdit',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      saving: false,
      widgetInfos: [],
      languages: [],
      segments:  [],
      referencedSegments: [],
      isCreating: false,

      currentIncentive: null,
      copyIncentive: null,
      singleUseCouponsEnabled: null,             
      maxConditionLength: null,                     

      minimumValue: null,
      limitType: null,
      useCustomDisplayText: false,

      codeUploadModalVisible: false,
      file: '',
      codeCount: null,
      codePreview: null,
      codeState: null,
      uniqueCodeName: null
    }
  },
  mounted() {
    NProgress.start()
    const isCreating = this.$route.name === 'CampaignEditOffersCreate'
    this.isCreating = isCreating
    const apiCalls = [
      this.api.getCurrentAccountPrefs(),
      this.api.getSegments(this.campaign.id),
      this.api.getWidgetInfos(), 
      this.api.getLanguageList(),
      this.api.getValueSymbolForCampaign(this.campaign.id)]
    if (!isCreating) {
      apiCalls.push(this.api.getIncentive(this.$route.params.offerId))
    }
    Promise.all(apiCalls)
      .then((data) => {
        this.singleUseCouponsEnabled = data[0].singleUseCoupons
        this.maxConditionLength = data[0].extendedOfferTextLengths ? 500 : 200
        this.segments = data[1]
        this.widgetInfos = data[2]
        this.languages = data[3]
        this.valueSymbol = data[4]
        if (isCreating) {
          this.currentIncentive = this.resetCurrentIncentive()
        } else {
          this.currentIncentive = data[5]
        }
        // console.log(this.currentIncentive)
        this.minimumValue = this.currentIncentive.minPurchase === -1 ? 'none' : 'amount'
        this.limitType = this.currentIncentive.maxRewards === -1 ? 'none' : 'fixed'
        this.referencedSegments = getSegmentList(this.currentIncentive, this.segments)
        this.useCustomDisplayText = this.getCustomDisplayTextBool() 
        this.initWatchers()
        this.copyIncentive = _.cloneDeep(this.currentIncentive)
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.loading = false
        NProgress.done()
      })
  },
  computed: {
    fixedAmountDisplayTextString() {
      return `${this.currentIncentive.valueSymbol}${this.currentIncentive.value} ${this.$t('Off')}`
    },
    availableVariants() {
      const variants = this.widgetInfos.reduce((list, info) => {
        if (info.variants.length) {
          list.push(...info.variants);
        }
        return list;
      }, []);
      return [...new Set(variants)]
    },
    formattedVariants() {
      const vs = this.availableVariants.map(v => {
        return {
          label: v,
          value: v
        }
      })
      vs.unshift({
        label: this.$t('(none)'),
        value: ''
      })
      return vs
    },
    currentConditionLength() {
      return this.currentIncentive ? (this.currentIncentive.condition || '').length : 0;
    },
    // maybe could be done with vee-validate
    variantError() {
      if (this.currentIncentive && this.currentIncentive.settings && this.currentIncentive.settings.classes) {
        for (const v of this.formattedVariants) {
          if (v.value === this.currentIncentive.settings.classes) {
            return ''
          }  
        }
        return this.$t('validation.variantError', { variant: `${this.currentIncentive.settings.classes}` })
      }
      return ''
    },
    conditionLengthError() {
      if (this.currentIncentive && this.currentIncentive.condition) {
        return this.currentConditionLength > this.maxConditionLength ? this.$t('campaignEdit.bulkEdit.conditionLengthError', { value: `${this.maxConditionLength}` }) : ''
      }
      return ''
    },
    hasChanged() {
      return !_.isEqual(this.currentIncentive, this.copyIncentive)
    }
  },
  methods: {
    showCodeModal() {
      this.codeUploadModalVisible = true
      this.$refs.file.click()
    },
    onSelectFile() {
      const file = this.$refs.file.files[0]
      this.file = file

      var overMaxSizeCount = 0
      var validCount = 0

      var formData = new FormData()

      if ( file.size < MAX_FILE_SIZE ) {
        formData.append('file', file)
        validCount++
      } else {
        overMaxSizeCount++
      }

      formData.append('count', validCount);

      if ( validCount ) {
        this.codeState = 'uploading'
        NProgress.start()
        this.api.uploadCodes(formData)
          .then((response) => {
            this.codeCount = response.count
            this.codePreview = response.preview
            this.uniqueCodeName = response.uniqueName
            this.codeState = 'uploaded'
            this.copyIncentive = _.cloneDeep(this.currentIncentive)
          })
          .catch((error) => {
            this.$ui.alert({ title: error.message })
          })
          .finally(() => {
            NProgress.done()
          })
      }

      if ( overMaxSizeCount ) {
        this.$ui.alert({ title: this.$t('File too big') })
      }
    },
    insertCodes() {
      this.codeState = 'uploading'
      this.api.insertCodes(this.currentIncentive.id, this.uniqueCodeName)
        .then((response) => {
          this.currentIncentive.maxRewards = response.maxRewards
          this.$ui.toast({ title: this.$t('campaignEdit.offer.xCodesAdded', [`${response.codesAdded}`]) })
        })
        .catch((error) => {
          this.$ui.alert({ title: error.message })
        })
        .finally(() => {
          this.codeUploadModalVisible = false
          this.codeState = null
          this.codeCount = null
        })  
    },
    initWatchers() {
      this.$watch('currentIncentive.value', (value) => {
        if (this.currentIncentive.valueType === '$') {
          const floatValue = parseFloat(value)
          if (isNaN(floatValue)) {
            this.currentIncentive.notionalValue = 0
          } else {
            this.currentIncentive.notionalValue = floatValue
          }
        }
        if (this.currentIncentive.valueType === 't' && !this.useCustomDisplayText) {
          this.currentIncentive.displayText = this.currentIncentive.value
        }
        if (this.currentIncentive.valueType === '$' && !this.useCustomDisplayText) {
          this.currentIncentive.displayText = this.fixedAmountDisplayTextString
        }
      })
      this.$watch('currentIncentive.valueType', () => {
        this.currentIncentive.value = ''
        this.currentIncentive.notionalValue = 0
        this.useCustomDisplayText = false
        this.updateDisplayText()
      })
      this.$watch('useCustomDisplayText', () => {
        this.updateDisplayText()
      })
    },
    updateDisplayText() {
      switch (this.currentIncentive.valueType) {
        case '$': this.currentIncentive.displayText = this.fixedAmountDisplayTextString; break;
        case '%': this.currentIncentive.displayText = this.$t('% Off'); break;
        case 's': this.currentIncentive.displayText = this.$t('Free Shipping'); break;
        case 't': this.currentIncentive.displayText = this.currentIncentive.value || ''; break;
      }
    },  
    getCustomDisplayTextBool() {
      if (this.currentIncentive.valueType === '$') {
        return this.currentIncentive.displayText !== this.fixedAmountDisplayTextString
      }
      if (this.currentIncentive.valueType === '%') {
        return this.currentIncentive.displayText !== this.$t('% Off')
      }
      if (this.currentIncentive.valueType === 's') {
        return this.currentIncentive.displayText !== this.$t('Free Shipping')
      }
      if (this.currentIncentive.valueType === 't') {
        return this.currentIncentive.displayText !== this.currentIncentive.value
      }
    },
    async backToList() {
      if (this.hasChanged) {
        const confirm = await this.$ui.confirm({
          title: this.$t('Unsaved Changes'),
          content: this.$t('campaignEdit.offer.unsavedChangesMessage'),
          primaryActionLabel: this.$t('Lose changes and return to list'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('Continue editing')
        })
        if (!confirm) {
          return
        }
      }
      this.$router.push(`/campaigns/${this.campaign.id}/offers`)
    },
    save() {
      NProgress.start()
      this.saving = true
      this.api.saveIncentive(this.processInc(), this.campaign.id)
        .then(() => {
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.copyIncentive = _.cloneDeep(this.currentIncentive)
          this.saving = false
          NProgress.done()
        })
    },
    processInc() {
      const inc = _.cloneDeep(this.currentIncentive)
      if (this.minimumValue === 'none') {
        inc.minPurchase = -1
      }
      if (this.limitType === 'none') {
        inc.maxRewards = -1
      }
      return inc
    },
    resetCurrentIncentive() {
      // probably a better way to structure this currentIncentive stuff
      return {
        inUpsellSegment: false,
        languageJson: {},
        linkUrl: '',
        linkText: '',
        displayText: '',
        presentedDuringSession: false,
        collectedDuringSession: false,
        calculatedPriority: null,
        notionalValue: '',
        timezone: this.theUser.accountTimezone,
        expiryDate: null,
        validFromDate: null,
        priority: 1,
        ifgOfferId: null,
        digitalRewardQuantity: null,
        digitalRewardType: null,
        isSessionCoupon: null,
        isDigitalReward: false,
        isUplift: false,
        customerTier: 'all',
        imageFile: '',
        status: 'active',
        isDisabled: false,
        campaignId: this.campaign.id,
        s3FileName: '',
        tempCode: null,
        promoCode: '',
        code: '',
        fileName: '',
        condition: '',
        id: '',
        value: '',
        numUsed: '',
        validFromDateT: null,
        expiryDateT: null,

        settings: {
          classes: ''
        },
        valueType: '$',
        language: this.campaign.language || window.PortalI18n.currentLocale || 'en',
        valueSymbol: this.valueSymbol,
        minPurchase: -1,
        maxRewards: -1,
        promoType: 'code',

        codeUploadCount: 0 // only used here
      }
    },
  },
  components: {
    ImageInput,
    CampaignEditHeader
  }
}

function getSegmentList(incentive, segments) {
  let segList = []
  segments.forEach((seg) => {
    if (getSegmentIds(incentive, segments).includes(seg.id)) {
      segList.push({
        id: seg.id,
        name: seg.name
      })
    }
  })
  return segList
}
function getSegmentIds(incentive, segments) {
  let segIds = []
  segments.forEach((seg) => {
    if (seg.offerIds) {
      seg.offerIds.forEach((oId) => {
        if (oId === incentive.id) {
          segIds.push(seg.id)
        } 
      })
    }
  })
  return segIds
}
</script>

<style lang="scss">
#CampaignEditOffersEdit {
  .modal-button {
    float: right;
  }
  .preview-list {
    margin: 10px 0px 10px 0px;
    list-style-type: none;
    padding: 15px;
    background-color: #eee;
  }
  .file-selector {
    margin: 10px 0px 0px 10px;
  }
  .modal-add-message {
    margin-bottom: 15px;
  }
}
</style>
